import { type Config } from "kadow-jetpack-model";
import { tt } from "../i18n/i18n";
import moment from "moment-timezone";
import { configAnimation } from "../routes/routes";
import { checkCTAsAreComplete } from "./checkCTAsAreComplete";

const MISSING_CTAS_ERROR = "add_ctas_required";
const MISSING_PRIZES_ERROR = "add_gifts_required";
const MISSING_GAMES_ERROR = "add_games_required";

export interface CompletenessCheckResult {
  isComplete: boolean;
  errorKey: string;
  errorMessage: string;
  btnText: string;
  link: string;
  hasGame?: boolean;
  hasPrize?: boolean;
  hasCta?: boolean;
}

/**
 * Checks if a jetpack configuration is complete and ready for testing.
 * A complete configuration must have:
 * - At least one CTA (call-to-action)
 * - At least one prize/gift
 * - At least one game (either permanent game or active event)
 *
 * @param jetpackConfig - The jetpack configuration to check
 * @param accountHasML - Whether the account has ML (Mystery Lottery) SaaS features
 * @returns CompletenessCheckResult containing isComplete status and error details
 */
export const checkJetpackCompleteness = (
  jetpackConfig: Config | null,
  accountHasML?: boolean
): CompletenessCheckResult => {
  if (!jetpackConfig) {
    return {
      isComplete: false,
      errorKey: MISSING_GAMES_ERROR,
      errorMessage: tt(MISSING_GAMES_ERROR),
      btnText: "",
      link: "",
      hasGame: false,
      hasPrize: false,
      hasCta: false
    };
  }

  const ctasAreOk = checkCTAsAreComplete(jetpackConfig);

  const giftsAreOk = !!jetpackConfig.prizes?.length;
  const nowTs = moment().unix();
  const nActiveEvents =
    jetpackConfig?.activeEvents?.filter((ae) => {
      return ae.sts <= nowTs && ae.ets >= nowTs;
    })?.length || 0;
  const gamesAreOk = !!jetpackConfig.games?.length || !!nActiveEvents;

  if (!ctasAreOk) {
    return {
      isComplete: false,
      errorKey: MISSING_CTAS_ERROR,
      errorMessage: tt(MISSING_CTAS_ERROR),
      btnText: tt("go_to_ctas_page"),
      link: `${configAnimation}/ctas`,
      hasGame: gamesAreOk,
      hasPrize: giftsAreOk,
      hasCta: ctasAreOk
    };
  }

  if (!giftsAreOk) {
    return {
      isComplete: false,
      errorKey: MISSING_PRIZES_ERROR,
      errorMessage: tt(MISSING_PRIZES_ERROR),
      btnText: tt("go_to_prizes_page"),
      link: `${configAnimation}/prizes`,
      hasGame: gamesAreOk,
      hasPrize: giftsAreOk,
      hasCta: ctasAreOk
    };
  }

  if (!gamesAreOk) {
    return {
      isComplete: false,
      errorKey: MISSING_GAMES_ERROR,
      errorMessage: tt(MISSING_GAMES_ERROR),
      btnText: tt("go_to_games_page"),
      link: `${configAnimation}/games`,
      hasGame: gamesAreOk,
      hasPrize: giftsAreOk,
      hasCta: ctasAreOk
    };
  }

  return {
    isComplete: true,
    errorKey: "",
    errorMessage: "",
    btnText: "",
    link: "",
    hasGame: gamesAreOk,
    hasPrize: giftsAreOk,
    hasCta: ctasAreOk
  };
};
